/**
 * Structured I/O row editor (DR1 B2/B4). Each row collapses to a summary header;
 * expand to edit label, description (mode-specific meaning), and required.
 *
 * - Inputs: description = what it is + typical source
 * - Outputs: description = what it is + output format (PDF, Excel, …)
 */

import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { ExpectedFieldRow, SetupCollectAt, SetupFieldType } from '@/types/aiStudioAgent';
import { SETUP_COLLECT_AT, SETUP_FIELD_TYPES } from '@/types/aiStudioAgent';
import { FIELD_CLASS, LABEL_CLASS } from './constants';
import { FieldLabel } from './FieldLabel';

export type ExpectedRowKind = 'input' | 'output';

interface ExpectedRowsEditorProps {
  label: string;
  hint?: string;
  /** Drives description label / placeholder (source vs output format). */
  kind: ExpectedRowKind;
  rows: ExpectedFieldRow[];
  disabled?: boolean;
  onChange: (rows: ExpectedFieldRow[]) => void;
}

const emptyRow = (): ExpectedFieldRow => ({
  label: '',
  description: '',
  required: false,
});

export const ExpectedRowsEditor: React.FC<ExpectedRowsEditorProps> = ({
  label,
  hint,
  kind,
  rows,
  disabled = false,
  onChange,
}) => {
  const { t } = useLanguage();
  // Track which rows are open for editing; newly added rows expand by default.
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const descriptionLabel =
    kind === 'input'
      ? t('aiStudio.definition.rowDescriptionAndSource')
      : t('aiStudio.definition.rowDescriptionAndFormat');
  const descriptionHint =
    kind === 'input'
      ? t('aiStudio.definition.rowDescriptionAndSourceHint')
      : t('aiStudio.definition.rowDescriptionAndFormatHint');
  const descriptionPlaceholder =
    kind === 'input'
      ? t('aiStudio.definition.rowDescriptionAndSourcePlaceholder')
      : t('aiStudio.definition.rowDescriptionAndFormatPlaceholder');

  useEffect(() => {
    setExpanded((current) => {
      const next = new Set<number>();
      current.forEach((index) => {
        if (index < rows.length) next.add(index);
      });
      return next;
    });
  }, [rows.length]);

  const toggleExpanded = (index: number) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateRow = (index: number, patch: Partial<ExpectedFieldRow>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
    setExpanded((current) => {
      const next = new Set<number>();
      current.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
  };

  const addRow = () => {
    const nextIndex = rows.length;
    onChange([...rows, emptyRow()]);
    setExpanded((current) => new Set(current).add(nextIndex));
  };

  return (
    <fieldset className="min-w-0">
      <FieldLabel as="span" tooltip={hint}>
        {label}
      </FieldLabel>

      <div className="flex flex-col gap-2">
        {rows.length === 0 && (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.definition.noRowsYet')}
          </p>
        )}
        {rows.map((row, index) => {
          const isOpen = expanded.has(index);
          const summary =
            row.label.trim() || t('aiStudio.definition.untitledRow');
          return (
            <div
              key={index}
              className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)]"
            >
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
                aria-expanded={isOpen}
                onClick={() => toggleExpanded(index)}
              >
                {isOpen ? (
                  <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden />
                )}
                <span className="min-w-0 flex-1 truncate font-medium">{summary}</span>
                {row.required && (
                  <span className="shrink-0 text-base text-[var(--color-surface-text-secondary)]">
                    {t('aiStudio.definition.rowRequired')}
                  </span>
                )}
              </button>

              {isOpen && (
                <div className="grid gap-2 border-t border-[var(--color-border)] p-3">
                  <div>
                    <label className={LABEL_CLASS}>
                      {t('aiStudio.definition.rowLabel')}
                    </label>
                    <input
                      className={FIELD_CLASS}
                      value={row.label}
                      disabled={disabled}
                      onChange={(e) => updateRow(index, { label: e.target.value })}
                    />
                  </div>
                  <div>
                    <FieldLabel
                      htmlFor={`io-description-${kind}-${index}`}
                      tooltip={descriptionHint}
                    >
                      {descriptionLabel}
                    </FieldLabel>
                    <textarea
                      id={`io-description-${kind}-${index}`}
                      className={FIELD_CLASS}
                      rows={3}
                      value={row.description}
                      disabled={disabled}
                      placeholder={descriptionPlaceholder}
                      onChange={(e) =>
                        updateRow(index, { description: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-base text-[var(--color-surface-text)]">
                      <input
                        type="checkbox"
                        checked={row.required ?? false}
                        disabled={disabled}
                        onChange={(e) =>
                          updateRow(index, { required: e.target.checked })
                        }
                      />
                      {t('aiStudio.definition.rowRequired')}
                    </label>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="inline-flex items-center justify-center rounded p-1 text-[var(--color-error)] hover:bg-[var(--color-surface-hover)]"
                        title={t('aiStudio.common.remove')}
                        aria-label={t('aiStudio.common.remove')}
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden />
                      </button>
                    )}
                  </div>

                  {kind === 'input' && (
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <FieldLabel tooltip={t('aiStudio.definition.rowKeyHint')}>
                          {t('aiStudio.definition.rowKey')}
                        </FieldLabel>
                        <input
                          className={FIELD_CLASS}
                          value={row.key ?? ''}
                          disabled={disabled}
                          placeholder={t('aiStudio.definition.rowKeyPlaceholder')}
                          onChange={(e) => updateRow(index, { key: e.target.value || undefined })}
                        />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>{t('aiStudio.definition.rowType')}</label>
                        <select
                          className={FIELD_CLASS}
                          value={row.type ?? ''}
                          disabled={disabled}
                          onChange={(e) =>
                            updateRow(index, {
                              type: (e.target.value || undefined) as SetupFieldType | undefined,
                            })
                          }
                        >
                          <option value="">{t('aiStudio.definition.rowTypeAuto')}</option>
                          {SETUP_FIELD_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>{t('aiStudio.definition.rowCollectAt')}</label>
                        <select
                          className={FIELD_CLASS}
                          value={row.collectAt ?? 'provision'}
                          disabled={disabled}
                          onChange={(e) =>
                            updateRow(index, { collectAt: e.target.value as SetupCollectAt })
                          }
                        >
                          {SETUP_COLLECT_AT.map((when) => (
                            <option key={when} value={when}>
                              {when}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className="mt-3 rounded-md border border-[var(--color-border)] px-3 py-1 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)]"
        >
          {t('aiStudio.definition.addRow')}
        </button>
      )}
    </fieldset>
  );
};
